import React from 'react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Separator } from '../ui/separator'
import { 
  Lightbulb, 
  Target, 
  Wand2, 
  ArrowRight, 
  Rocket, 
  Users, 
  Download, 
  Upload, 
  Crown, 
  Star, 
  Activity 
} from 'lucide-react'
import { RECENT_ACTIVITY, TOOLBOX_CATEGORIES, getActivityIcon } from './brandDashboardData'

interface OverviewTabProps {
  onGenerateAssets: () => void
  onInviteTeam: () => void
  onUpgradeToPro: () => void
}

export function OverviewTab({ onGenerateAssets, onInviteTeam, onUpgradeToPro }: OverviewTabProps) {
  return (
    <div className="space-y-8">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recommended Next Steps</CardTitle>
            <Lightbulb className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Target className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Complete Typography System</p>
                <p className="text-xs text-blue-600">Define your font hierarchy and usage guidelines.</p>
                <Button size="sm" variant="outline" className="mt-2 border-blue-300 text-blue-700 h-7 text-xs">
                  Start Now
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <Wand2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-800">Generate Marketing Assets</p>
                <p className="text-xs text-purple-600">Create business cards, social templates, and more with AI.</p>
                <Button size="sm" variant="outline" className="mt-2 border-purple-300 text-purple-700 h-7 text-xs">
                  Generate Assets
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
            <Rocket className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              size="sm" 
              onClick={onGenerateAssets}
              className="w-full justify-start bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Wand2 className="w-3 h-3 mr-2" />
              Generate AI Assets
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full justify-start"
              onClick={onInviteTeam}
            >
              <Users className="w-3 h-3 mr-2" />
              Invite Team
            </Button>
            <Button size="sm" variant="outline" className="w-full justify-start">
              <Download className="w-3 h-3 mr-2" />
              Export Guidelines
            </Button>
            <Button size="sm" variant="outline" className="w-full justify-start">
              <Upload className="w-3 h-3 mr-2" />
              Upload Assets
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-800">Pro Features</CardTitle>
            <Crown className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-xs text-blue-700 mb-3">
              <li className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                Unlimited AI generation
              </li>
              <li className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                High-res downloads
              </li>
              <li className="flex items-center gap-1">
                <Star className="w-3 h-3" />
                Custom domains
              </li>
            </ul>
            <Button 
              size="sm" 
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              onClick={onUpgradeToPro}
            >
              <Crown className="w-3 h-3 mr-1" />
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Recent Activity
              </CardTitle>
              <CardDescription>
                Track progress and team collaboration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {RECENT_ACTIVITY.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{activity.action}</p>
                      <p className="text-xs text-slate-600">{activity.details}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500">{activity.user}</span>
                        <span className="text-xs text-slate-400">•</span>
                        <span className="text-xs text-slate-500">{activity.timestamp}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <Button variant="outline" className="w-full">
                View All Activity
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Toolbox Progress by Category
            </CardTitle>
            <CardDescription>
              Track completion across all brand development phases
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {TOOLBOX_CATEGORIES.map((category) => (
                <div key={category.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={`${category.color} text-xs`}>
                      {category.name}
                    </Badge>
                    <span className="text-sm font-medium">{category.progress}%</span>
                  </div>
                  <Progress value={category.progress} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}